import { useState, type ReactNode } from 'react';
import { erc20Abi } from '@syntra/abi';
import type { Address } from 'viem';
import { useConnection, useReadContracts, useSwitchChain } from 'wagmi';
import { CHAIN } from '../../config/wagmi.ts';
import { EXPLORER } from '../../config/env.ts';
import { useFee } from '../../hooks/useFee.ts';
import { useI18n } from '../../i18n/index.tsx';
import { fmtFee } from '../../lib/format.ts';
import { Button, Notice } from '../ui/primitives.tsx';
import { useToast } from '../ui/toasts.tsx';
import { useConnectModal } from '../layout/ConnectModal.tsx';
import { readUntil } from './refresh.ts';
import { CatchUp } from './CatchUp.tsx';
import { useTx, type TxRequest } from './useTx.ts';

export interface Approval {
  token: Address;
  spender: Address;
  amount: bigint;
  symbol: string;
}

/**
 * The single action button every form uses: connect → right network → approve (exact amount, one token at a time,
 * in the order given) → action.
 * `blocked` is a reason the action cannot run yet (empty amount, insufficient balance…), shown on the button.
 */
export function ActionButton({
  label,
  request,
  blocked,
  approve,
  onDone,
  variant = 'primary',
}: {
  label: ReactNode;
  request: TxRequest | null;
  blocked?: string | null;
  approve?: Approval | Approval[];
  onDone?: () => void;
  variant?: 'primary' | 'secondary';
}) {
  const { t } = useI18n();
  const toast = useToast();
  const { address, chainId, isConnected } = useConnection();
  const { switchChain } = useSwitchChain();
  const openConnect = useConnectModal();
  const tx = useTx();
  const approveTx = useTx();
  const [syncing, setSyncing] = useState(false); // approved on chain, waiting for the RPC to show the allowance
  const approvals = (Array.isArray(approve) ? approve : approve ? [approve] : []).filter((a) => a.amount > 0n);
  const allowances = useReadContracts({
    allowFailure: false,
    contracts: approvals.map((a) => ({ address: a.token, abi: erc20Abi, functionName: 'allowance', args: [address!, a.spender] }) as const),
    query: { enabled: Boolean(address) && approvals.length > 0 },
  });
  const pendingApproval = approvals.find((a, i) => (allowances.data?.[i] ?? 0n) < a.amount);
  // the fee of whatever the button will send next: the approval while one is due, the action itself after it
  const ready = isConnected && chainId === CHAIN.id && !blocked;
  const fee = useFee(
    pendingApproval
      ? { address: pendingApproval.token, abi: erc20Abi, functionName: 'approve', args: [pendingApproval.spender, pendingApproval.amount] }
      : request,
    ready,
  );

  // Reported from the click's own promise, not from an effect on this component: the refresh that runs before
  // "done" can remove this very button (a withdrawn lot, a claimed bond), and an unmounted effect never fires.
  const runAction = async (req: TxRequest) => {
    const hash = await tx.run(req);
    if (!hash) return;
    toast(
      'ok',
      <span>
        {t('common.done')}
        {EXPLORER ? (
          <>
            {' · '}
            <a href={`${EXPLORER}/tx/${hash}`} target="_blank" rel="noreferrer">
              {t('common.viewTx')}
            </a>
          </>
        ) : null}
      </span>,
    );
    onDone?.();
    tx.reset();
  };

  if (!isConnected) {
    return (
      <Button block onClick={openConnect}>
        {t('wallet.connect')}
      </Button>
    );
  }
  if (chainId !== CHAIN.id) {
    return (
      <Button block variant="secondary" onClick={() => switchChain({ chainId: CHAIN.id })}>
        {t('network.switch', { chain: CHAIN.name })}
      </Button>
    );
  }

  const busy = tx.busy || approveTx.busy || syncing;
  const phaseLabel =
    tx.phase === 'confirm' || approveTx.phase === 'confirm'
      ? t('common.confirmInWallet')
      : tx.phase === 'pending' || approveTx.phase === 'pending' || syncing
        ? t('common.pending')
        : null;

  return (
    <div className="field">
      {pendingApproval && !blocked ? (
        <Button
          block
          variant={variant}
          disabled={busy}
          onClick={async () => {
            const h = await approveTx.run({
              address: pendingApproval.token,
              abi: erc20Abi,
              functionName: 'approve',
              args: [pendingApproval.spender, pendingApproval.amount],
            });
            if (h) {
              // one re-read can still return the old allowance, which would ask for the approval a second time
              const i = approvals.indexOf(pendingApproval);
              setSyncing(true);
              try {
                await readUntil(
                  () => allowances.refetch(),
                  (r) => (r.data?.[i] ?? 0n) >= pendingApproval.amount,
                );
              } finally {
                setSyncing(false);
              }
              toast('ok', t('common.approved', { token: pendingApproval.symbol }));
            }
          }}
        >
          {phaseLabel ?? t('common.approve', { token: pendingApproval.symbol })}
        </Button>
      ) : (
        <Button block variant={variant} disabled={busy || Boolean(blocked) || !request} onClick={() => request && void runAction(request)}>
          {phaseLabel ?? blocked ?? label}
        </Button>
      )}
      {fee !== null ? (
        <div className="muted fee-line" data-testid="fee">
          {t('common.fee', { amount: fmtFee(fee) })}
        </div>
      ) : null}
      {tx.error || approveTx.error ? <Notice tone="error">{tx.error ?? approveTx.error}</Notice> : null}
      {tx.errorName === 'RebaseBacklog' ? <CatchUp onDone={tx.reset} /> : null /* the way out of the backlog, right under the reason */}
    </div>
  );
}
